import useAuth from './useAuth';
import useIsMobile from './useIsMobile';
import { overridesFor } from './accessOverrides';

/**
 * Central capability flags derived from the signed-in user's role(s). This is
 * the single place the permission matrix lives — components should consult
 * these flags instead of checking raw role booleans, so the rules stay
 * consistent and auditable. See SPEC_roles-and-permissions.md for the matrix.
 *
 * NOTE on plant scoping: these flags say WHETHER plant scoping applies
 * (`scopedToAssignedPlant`, `partsEditScopedToPlant`). The actual filtering by
 * the user's assigned plant codes is applied where lists are built (it needs
 * the user's Proxemics Team record, fetched there), not in this hook.
 *
 * Roles (Entra app-role values): Director, Executive, RegionalManager,
 * LiaisonManager, Liaison.
 */
export default function usePermissions() {
  const auth = useAuth();
  const {
    isDirector,
    isExecutive,
    isRegionalManager,
    isLiaisonManager,
  } = auth;

  // On phone/tablet widths the portal is intentionally view-only for
  // everything EXCEPT My Account and the Billing Tracker (those stay fully
  // usable — see Timesheets/Adjustments + MyAccount, which don't gate on
  // these flags). `w` ("writable") forces an edit/add capability off on
  // mobile while leaving it untouched on desktop, so every button gated by
  // these flags disappears on a phone from this one place.
  const isMobile = useIsMobile();
  const w = (cap) => cap && !isMobile;

  // Per-user exceptions to the role matrix (see accessOverrides.js). These ADD
  // capabilities to specific people on top of their role; they never remove
  // any. `o(flag)` is true when this user has that capability granted as a
  // custom override.
  const ovr = overridesFor(auth.email);
  const o = (cap) => ovr[cap] === true;

  return {
    ...auth,

    // Expose the mobile flag so components with bespoke (non-flag) edit
    // gating (e.g. isDirector-only buttons) can hide on mobile too.
    isMobile,

    // ---- Top-level section access (nav + routes) ----
    // Visibility is NOT restricted on mobile — you can still open every
    // page you're allowed to see; you just can't edit/add there.
    canSeeBillingManager: isDirector,
    canSeeInvoices: isDirector,
    canSeeSales: isDirector || o('canSeeSales'),

    // Create / edit / send sales proposals on the Sales page. Director by
    // default; can be granted to specific people via accessOverrides.js.
    // (This is the proposal pipeline only — the Director-only Sales Resource
    // upload library is gated separately by `canUpload`.)
    canManageProposals: isDirector || o('canManageProposals'),

    // ---- Create / edit / add (disabled on mobile) ----
    // Edit an existing plant — Director or Regional Manager (RMs are still
    // scoped to their assigned plants via usePlantScope).
    canEditPlants: w(isDirector || isRegionalManager),
    // Add a NEW plant — Director only (per Jillian, Aug 2026). RMs can edit
    // their assigned plants but can't create new ones.
    canAddPlants: w(isDirector),
    // Delete a plant — Director only (per Jillian, Sep 2026). Deletion is
    // permanent and Parts / Suppliers / Team records that reference the
    // plant code are NOT cleaned up, so this stays the narrowest gate.
    canDeletePlants: w(isDirector),
    canEditSuppliers: w(isDirector || o('canEditSuppliers')),
    canAddSuppliers: w(isDirector || o('canAddSuppliers')),
    // Parts stay fully editable on mobile — liaisons often add a new part
    // right from the plant floor, so this is intentionally NOT gated by
    // the mobile read-only rule.
    canEditParts: isDirector || isRegionalManager || isLiaisonManager,
    canAddParts: isDirector || isRegionalManager || isLiaisonManager,
    canAddTeam: w(isDirector),
    canEditTeam: w(isDirector),

    // ---- Uploads: any file-upload control is Director-only (off on mobile) ----
    canUpload: w(isDirector),

    // ---- Dashboard News & Announcements (off on mobile) ----
    canEditNews: w(isDirector || isExecutive || isRegionalManager),

    // ---- Billing Tracker (Timesheets) tabs + actions ----
    // Submit: Director, RM, LM (Executive cannot submit).
    canSubmitTimesheets: isDirector || isRegionalManager || isLiaisonManager,
    // Approvals tab visibility: Director, RM, Executive (view); LM has no
    // approvals tab.
    canViewApprovals: isDirector || isExecutive || isRegionalManager,
    // Acting on approvals (approve / revise): Director, RM only.
    // Executive sees the tab read-only.
    canApproveTimesheets: isDirector || isRegionalManager,
    // Adjustments tab: Director, RM, LM (not Executive).
    canSeeAdjustments: isDirector || isRegionalManager || isLiaisonManager,

    // ---- Visibility / scoping ----
    // Regional Managers and Liaison Managers only see plants they're assigned
    // to (the plant codes on their Team record). Executive and Director see
    // all plants. The actual filtering is applied where the Plants list/detail
    // are built — see usePlantScope().
    scopedToAssignedPlant: isLiaisonManager || isRegionalManager,
    // RM and LM can only edit/add Parts at their assigned plant(s).
    partsEditScopedToPlant: isRegionalManager || isLiaisonManager,

    // ---- Detail-page limits ----
    // Liaison Manager sees a reduced set of sections on Plant/Team detail
    // pages (Basic Info, Role Designation & Duties, Plant Assignment, Org
    // Structure) and cannot print the Plant PDF.
    limitedDetailSections: isLiaisonManager,
    canPrintPlantPdf: isDirector || isRegionalManager || isExecutive,
  };
}
